<template>
    <b-card class="console-section mb-3" no-body>
        <div class="section-head d-flex align-items-center justify-content-between px-3 py-2">
            <div>
                <span class="section-index">①</span>
                <span class="fw-500 ml-1">신청 질문</span>
                <span class="text-secondary small ml-2">신청자가 답하는 항목 ({{ inputKeys.length }})</span>
            </div>
            <div class="flex-shrink-0">
                <b-button size="sm" variant="outline-secondary" class="mr-1" @click="$emit('reorder')">
                    순서 변경
                </b-button>
                <b-button size="sm" variant="primary" @click="$emit('add')">
                    질문 추가
                </b-button>
            </div>
        </div>

        <div class="p-3">
            <!-- 규칙이 참조하지만 신청서에 없는 질문 (readiness.missing_keys) -->
            <div v-if="missingKeys.length" class="mb-3">
                <div
                    v-for="key in missingKeys"
                    :key="`missing-${key}`"
                    class="missing-row d-flex align-items-center justify-content-between px-3 py-2 mb-1"
                >
                    <div>
                        <i class="bi bi-exclamation-triangle-fill text-danger mr-2"></i>
                        <span class="fw-500">{{ key }}</span>
                        <span class="text-danger small ml-2">배정 규칙이 참조하지만 신청서에 없는 질문입니다.</span>
                    </div>
                    <b-button size="sm" variant="outline-danger" @click="$emit('create-missing', key)">
                        질문 만들기
                    </b-button>
                </div>
            </div>

            <p-page-empty-component v-if="inputKeys.length === 0" compact text="아직 만든 신청 질문이 없습니다."/>

            <b-table-simple v-else responsive hover class="mb-0 question-table">
                <b-thead>
                    <b-tr>
                        <b-th style="width: 34%">질문 문구</b-th>
                        <b-th style="width: 12%">답변 형식</b-th>
                        <b-th style="width: 8%">필수</b-th>
                        <b-th style="width: 26%">선택지</b-th>
                        <b-th style="width: 12%" class="text-right">관리</b-th>
                    </b-tr>
                </b-thead>
                <b-tbody>
                    <b-tr v-for="q in sortedKeys" :key="q.id">
                        <b-td>
                            <div class="fw-500">{{ labelOf(q) }}</div>
                            <div class="text-secondary small">{{ q.key_name }}</div>
                        </b-td>
                        <b-td>
                            <b-badge variant="light" class="border">{{ valueTypeText(q.value_type) }}</b-badge>
                        </b-td>
                        <b-td>
                            <b-badge v-if="q.is_required" variant="warning">필수</b-badge>
                            <span v-else class="text-secondary small">선택</span>
                        </b-td>
                        <b-td>
                            <template v-if="q.value_type === 'list'">
                                <b-badge
                                    v-for="av in (q.allowed_values || []).slice(0, 4)"
                                    :key="av.id || av.actual_value"
                                    variant="light"
                                    class="border mr-1 mb-1"
                                >{{ choiceLabel(av) }}</b-badge>
                                <span v-if="(q.allowed_values || []).length > 4" class="text-secondary small">
                                    외 {{ q.allowed_values.length - 4 }}개
                                </span>
                                <span v-if="(q.allowed_values || []).length === 0" class="text-secondary small">
                                    선택지 없음
                                </span>
                            </template>
                            <span v-else class="text-secondary small">-</span>
                        </b-td>
                        <b-td class="text-right">
                            <b-button size="sm" variant="link" class="p-0" @click="$emit('edit', q)">편집</b-button>
                        </b-td>
                    </b-tr>
                </b-tbody>
            </b-table-simple>
        </div>
    </b-card>
</template>

<script>
import {valueTypeText} from './plain-language';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';

export default {
    name: 'QuestionSection',
    components: {
        'p-page-empty-component': PageEmptyComponent,
    },
    props: {
        // 신청 질문 목록 (schema 응답의 input_keys)
        inputKeys: {type: Array, default: () => []},
        // 규칙이 참조하지만 신청서에 없는 질문 이름들 (readiness.missing_keys)
        missingKeys: {type: Array, default: () => []},
    },
    computed: {
        sortedKeys() {
            return [...this.inputKeys].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        },
    },
    methods: {
        valueTypeText,
        labelOf(q) {
            const lt = q.label_texts || {};
            return lt.ko || q.key_name;
        },
        choiceLabel(av) {
            const lt = av.label_texts || {};
            return lt.ko || av.actual_value;
        },
    },
};
</script>

<style lang="scss" scoped>
.console-section {
    border-radius: 8px;
}

.section-head {
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.section-index {
    font-weight: 700;
    color: #6c757d;
}

.missing-row {
    background: #fff5f5;
    border: 1px solid #f5c2c7;
    border-radius: 6px;
}

.question-table ::v-deep th {
    font-size: 0.85rem;
    color: #6c757d;
    font-weight: 500;
}
</style>
